/**
 * 카카오모먼트 광고 성과 연동 설정 — 광고계정 ID 입력 → 카카오 동의(OAuth) → 상태 확인.
 *
 * 흐름은 틱톡 연동 화면(`setup/tiktok.tsx`)과 같다: 버튼 → 팝업 동의 → 팝업 닫힘 감시 → 상태 재조회.
 * 다른 점은 **광고계정 ID 를 동의 전에 받는다**는 것이다 — 카카오 동의 화면은 우리가 넘긴 광고계정에 대한
 * 동의를 받고, 콜백이 그 계정을 조회해 확인한다. 그래서 "계정 선택 대기" 상태가 없다.
 *
 * ⚠️ 카카오 동의 화면에는 권한이 '운영 권한'으로 보인다. 실제로는 성과 조회만 하므로 연결 버튼 근처에
 *    안심 문구를 둔다.
 * ⚠️ 해제 시 카카오 쪽 철회는 서버 스위치(`KAKAO_REVOKE_ENABLED`)로 기본 꺼져 있다 — `revoked_at_kakao` 는
 *    사용자에게 따로 알리지 않는다.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ExternalLink, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { getGetShopQueryKey } from '@/api/generated/v2-shop/v2-shop'
import {
  disconnectKakao,
  getGetKakaoStatusQueryKey,
  getKakaoStatus,
  kakaoOAuthStart,
  useGetKakaoStatus,
} from '@/api/generated/v2-kakao/v2-kakao'
import { TipBox } from '@/components/guide/TipBox'
import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SetupPage } from '@/features/setup/SetupPage'
import { useConfirm } from '@/hooks/useConfirm'
import { apiMessage } from '@/lib/apiError'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'

export const Route = createFileRoute('/_dashboard/setup/kakao')({
  component: KakaoSetupPage,
})

/** 광고계정 ID 는 숫자만 — 서버도 같은 규칙으로 422 를 준다(헛걸음 방지용 사전 판정) */
const AD_ACCOUNT_ID_PATTERN = /^\d{1,32}$/

/**
 * ⭐ 히든 게이트 — 카카오 게이트(전역 스위치 ∧ 실험실 플래그)가 닫힌 샵이면 **첫 줄에서** 잘라낸다.
 *    본문 컴포넌트를 분리해 둬서 허용 밖이면 쿼리도 나가지 않는다(틱톡 화면과 같은 구조).
 * ⚠️ 이 플래그는 노출용이다 — 실제 차단은 `/v2/kakao/*` 404(`v2/kakao/gate.py`)가 한다.
 */
function KakaoSetupPage() {
  const { shop } = useShop()
  if (!shop?.kakao_enabled) return <HiddenFeatureNotFound />
  return <KakaoSetupContent />
}

/** react-query 에러에서 HTTP 상태를 꺼낸다 — custom-instance 는 두 모양 중 하나로 던진다. */
function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; response?: { status?: number } } | null
  return e?.status ?? e?.response?.status
}

function KakaoSetupContent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  // 입력 중인 광고계정 ID — 만료 시 "다시 연동" 이 저장된 ID 로 채운다
  const [adAccountId, setAdAccountId] = useState('')

  useEffect(() => {
    if (!auth.isAuthenticated()) navigate({ to: '/' })
  }, [navigate])

  const {
    data: status,
    isLoading,
    error: statusError,
    refetch: refetchStatus,
    isFetching: statusFetching,
  } = useGetKakaoStatus({
    query: {
      enabled: auth.isAuthenticated(),
      // 404(게이트 밖)는 재시도하지 않는다 — 일시적 5xx·네트워크 오류는 그대로 재시도한다
      retry: (count, err) => errorStatus(err) !== 404 && count < 3,
    },
  })
  // 플래그가 늦게 반영된 경우(샵 응답 캐시 등)에도 API 가 404 면 같은 화면으로 떨어뜨린다
  const statusNotFound = errorStatus(statusError) === 404
  // ⚠️ 상태를 **모르는** 것과 미연동은 다르다 — 조회 실패를 "미연동" 으로 그리면 연동된 샵이
  //    재연동하러 간다. 그때는 에러와 재시도만 그린다(틱톡 화면과 같은 이유).
  const statusUnknown = !!statusError && !status && !statusNotFound

  // 상태와 샵 응답은 함께 낡는다 — 사이드바 완료·경고 표시가 `shop.kakao` 요약을 읽는다.
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetKakaoStatusQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
  }

  // 팝업이 아니라 같은 탭으로 돌아온 경우의 복귀 신호 처리
  // (콜백 `_close_popup_response` 가 `/setup/kakao?kakao_return=1…` 로 보낸다).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('kakao_return') !== '1') return
    if (params.get('kakao_ok') === '1') {
      toast.success('카카오모먼트 광고계정이 연동되었어요')
    } else {
      toast.error(callbackErrorMessage(params.get('kakao_err')), { duration: 8000 })
    }
    queryClient.invalidateQueries({ queryKey: getGetKakaoStatusQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
    // 새로고침해도 토스트가 반복되지 않게 쿼리스트링을 지운다
    window.history.replaceState({}, '', window.location.pathname)
  }, [queryClient])

  const connected = !!status?.connected
  // 연동됨과 자격증명 유효성은 다른 축이다 — 만료면 재연동 입력란을 다시 연다
  const credentialInvalid = connected && !!status?.invalid
  const savedAdAccountId = status?.ad_account_id ?? null

  // 만료 상태에서는 저장된 광고계정 ID 를 입력란에 채워 둔다 — 같은 계정으로 바로 다시 동의할 수 있게.
  // 저장값 변화에만 반응하므로 입력 중에 값이 되돌아가지 않는다.
  useEffect(() => {
    if (credentialInvalid && savedAdAccountId) setAdAccountId(savedAdAccountId)
  }, [credentialInvalid, savedAdAccountId])

  // ⚠️ 버튼을 눌렀을 때만 부른다 — state 에 TTL 이 있어 미리 만들어 두면 늦게 누른 사용자가
  //    `invalid_state` 로 떨어진다(틱톡·구글 화면과 같은 이유).
  const connectMutation = useMutation({
    mutationFn: (id: string) => kakaoOAuthStart({ ad_account_id: id }),
    onSuccess: ({ authorize_url }) => {
      // 팝업이 막히면 같은 탭으로 진행한다 — 콜백이 두 경우 모두를 처리한다.
      const popup = window.open(authorize_url, 'kakao-oauth', 'width=520,height=720')
      if (!popup) {
        window.location.href = authorize_url
        return
      }
      // ⚠️ 팝업이 정상적으로 닫히면 부모는 아무 신호를 못 받는다. `postMessage` 는 COOP 로
      //    `window.opener` 가 끊길 수 있어 쓰지 않고, **우리가 연 창의 `closed`** 를 감시한다.
      const timer = window.setInterval(() => {
        if (!popup.closed) return
        window.clearInterval(timer)
        refreshAll()
        // ⚠️ 팝업 경로의 실패는 **사유가 부모에 오지 않는다** — 콜백 HTML 이 먼저 `window.close()` 를
        //    하므로 같은 탭 복귀(`kakao_err`)가 일어나지 않는다. 닫힌 뒤에도 미연동이면 흔한 원인을 짚어 준다.
        queryClient
          .fetchQuery({ queryKey: getGetKakaoStatusQueryKey(), queryFn: () => getKakaoStatus() })
          .then((next) => {
            // 만료 상태에서 재연동이 실패하면 connected 는 참인 채 invalid 가 남는다 — 그것도 실패로 본다
            if (!next.connected || next.invalid) {
              toast.error(
                '연동이 완료되지 않았어요. 광고계정 ID 와, 그 광고계정의 마스터 또는 멤버 권한이 있는 카카오계정으로 동의했는지 확인해 주세요',
                { duration: 8000 },
              )
            } else {
              toast.success('카카오모먼트 광고계정이 연동되었어요')
            }
          })
          .catch(() => {})
      }, 500)
      // 팝업을 열어둔 채 잊어버려도 타이머가 남지 않게 30분 뒤 정리한다.
      window.setTimeout(() => window.clearInterval(timer), 30 * 60 * 1000)
    },
    // 422 메시지는 서버가 고객 언어로 준다(형식 오류·서버 미설정·다른 쇼핑몰에 연결된 계정)
    onError: (e: unknown) => toast.error(apiMessage(e, '카카오 연동을 시작하지 못했어요')),
  })

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectKakao(),
    onSuccess: () => {
      refreshAll()
      setAdAccountId('')
      toast.success('카카오모먼트 연동이 해제되었어요')
    },
    // 거절은 화면 상태가 낡았다는 뜻일 수 있다 — 다시 읽는다
    onError: (e: unknown) => {
      toast.error(apiMessage(e, '연동을 해제하지 못했어요'))
      refreshAll()
    },
  })

  const trimmedId = adAccountId.trim()
  const idValid = AD_ACCOUNT_ID_PATTERN.test(trimmedId)
  // 입력이 있는데 숫자가 아니면 바로 알려 준다 — 빈 칸일 때는 조용히 둔다
  const idError = trimmedId && !idValid ? '숫자만 입력해 주세요' : undefined
  // 연결 전 또는 만료 상태에서만 입력란을 보인다(정상 연결 상태에서 계정을 바꾸려면 해제 후 다시 연동)
  const showConnectForm = !connected || credentialInvalid

  const guide = (
    <div className="space-y-4 text-sm text-on-surface-variant">
      <p>
        카카오모먼트 광고계정을 연동하면 캠페인·광고별 성과(노출·클릭·비용)를 매일 자동으로
        불러와요. 성과 조회만 하고 광고 설정은 바꾸지 않아요.
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>카카오모먼트 광고 관리 화면 상단의 광고계정 번호를 확인해 주세요.</li>
        <li>번호를 입력하고 &lsquo;카카오로 연동&rsquo; 버튼을 눌러 동의해 주세요.</li>
        <li>
          동의는 그 광고계정의 마스터 또는 멤버 권한이 있는 카카오계정으로 해야 해요.
        </li>
      </ol>
    </div>
  )

  if (statusNotFound) return <HiddenFeatureNotFound />

  if (statusUnknown) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4">
        <TipBox type="danger">
          <p>
            {apiMessage(statusError, '카카오 연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}
          </p>
          <button
            type="button"
            onClick={() => refetchStatus()}
            disabled={statusFetching}
            className="mt-2 min-h-[44px] sm:min-h-0 text-sm font-medium underline"
          >
            다시 시도
          </button>
        </TipBox>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-on-surface-variant" />
      </div>
    )
  }

  return (
    <SetupPage
      title="카카오 광고 연동"
      connected={connected}
      warningBadge={credentialInvalid ? '재연동 필요' : undefined}
      externalLink="https://moment.kakao.com/"
      externalLinkLabel="카카오모먼트 열기"
      guide={guide}
      // 연동 해제 — 공통 틀 ⑤(맨 아래 회색 텍스트). 규칙은 agent_docs/connect_ux.md §2
      disconnect={
        connected
          ? {
              description: '연동을 해제하면 카카오 광고 성과 수집이 멈춰요.',
              loading: disconnectMutation.isPending,
              onClick: async () => {
                const ok = await confirm(
                  '카카오 광고 성과 수집이 중단돼요. 다시 쓰려면 광고계정 ID 를 입력하고 새로 연동해야 해요.',
                  { title: '카카오 연동을 해제할까요?', confirmText: '연동 해제', variant: 'danger' },
                )
                if (ok) disconnectMutation.mutate()
              },
            }
          : undefined
      }
    >
      <div className="space-y-6">
        {/* 재조회가 실패했지만 직전 상태는 있는 경우 — 보이는 상태가 낡았을 수 있음을 알린다 */}
        {statusError && !statusNotFound && (
          <TipBox type="danger">
            {apiMessage(statusError, '연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}
          </TipBox>
        )}

        {credentialInvalid && (
          <TipBox type="danger">
            <p className="font-medium">카카오 연동이 만료되었어요.</p>
            <p className="mt-1">
              광고 성과를 더 이상 불러오지 못해요. 아래 &lsquo;카카오로 다시 연동&rsquo; 버튼으로 다시
              동의해 주세요.
            </p>
          </TipBox>
        )}

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-on-surface">광고계정</h2>
            {!connected ? (
              <Badge variant="secondary">미연동</Badge>
            ) : credentialInvalid ? (
              <Badge variant="warning">재연동 필요</Badge>
            ) : (
              <Badge variant="success">연동됨</Badge>
            )}
          </div>

          {/* 연결된 광고계정 — 이름이 없으면 ID 만 보인다 */}
          {connected && savedAdAccountId && (
            <p className="text-sm text-on-surface-variant break-all">
              연결된 광고계정:{' '}
              <span className="font-medium text-on-surface">
                {status?.ad_account_name
                  ? `${status.ad_account_name} (${savedAdAccountId})`
                  : savedAdAccountId}
              </span>
            </p>
          )}

          {connected && !credentialInvalid && (
            <p className="text-xs text-on-surface-variant">
              다른 광고계정으로 바꾸려면 연동을 해제한 뒤 새 광고계정 ID 로 다시 연동해 주세요.
            </p>
          )}

          {showConnectForm && (
            <form
              className="space-y-3 max-w-md"
              onSubmit={(e) => {
                e.preventDefault()
                if (idValid && !connectMutation.isPending) connectMutation.mutate(trimmedId)
              }}
            >
              <div className="space-y-1">
                <Input
                  label="카카오모먼트 광고계정 ID"
                  value={adAccountId}
                  onChange={(e) => setAdAccountId(e.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="예: 123456"
                  maxLength={32}
                  error={idError}
                />
                <p className="text-xs text-on-surface-variant">
                  카카오모먼트 광고 관리 화면 상단의 광고계정 번호예요. 광고계정의 마스터 또는 멤버
                  권한이 있는 카카오계정으로 동의해야 해요.
                </p>
              </div>

              <div className="space-y-2">
                <Button
                  type="submit"
                  variant="primary"
                  loading={connectMutation.isPending}
                  disabled={!idValid}
                  className="min-h-[44px] sm:min-h-0"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  {credentialInvalid ? '카카오로 다시 연동' : '카카오로 연동'}
                </Button>
                {/* 카카오 동의 화면에 '운영 권한'으로 보여 놀라지 않게 — 실제로는 조회만 한다 */}
                <p className="text-xs text-on-surface-variant">
                  성과 조회만 하고 광고 설정은 바꾸지 않아요. 카카오 동의 화면에 권한 이름이 넓게
                  보여도 광고를 만들거나 수정하지 않아요.
                </p>
              </div>
            </form>
          )}
        </section>
      </div>
      <ConfirmDialog />
    </SetupPage>
  )
}

/**
 * 콜백 실패 사유를 고객 언어로. 모르는 코드는 일반 문구로 흘린다.
 * ⚠️ 서버 `v2/kakao/router.py::_SAFE_CALLBACK_ERRORS` 와 **1:1** 로 맞춘다 — 서버에 코드를 추가하면 여기도.
 */
function callbackErrorMessage(code: string | null): string {
  switch (code) {
    case 'consent_denied':
      return '카카오 동의를 취소했어요. 다시 시도해 주세요'
    case 'invalid_state':
      return '연동 요청이 만료됐어요. 다시 시도해 주세요'
    case 'missing_parameters':
      return '카카오에서 필요한 정보가 오지 않았어요. 다시 시도해 주세요'
    case 'not_configured':
      return '카카오모먼트 연동이 서버에 설정되어 있지 않아요 (관리자 문의)'
    case 'token_exchange_failed':
      return '카카오 인증을 마치지 못했어요. 다시 시도해 주세요'
    case 'kakao_unavailable':
      return '카카오에 연결하지 못했어요. 잠시 후 다시 시도해 주세요'
    case 'account_not_agreed':
      return '입력한 광고계정에 대한 동의가 확인되지 않았어요. 광고계정 ID 를 확인하고 다시 시도해 주세요'
    case 'account_not_accessible':
      return '이 카카오계정으로는 입력한 광고계정을 조회할 수 없어요. 광고계정의 마스터 또는 멤버 권한이 있는 카카오계정으로 동의해 주세요'
    case 'account_in_use':
      return '이 광고계정은 이미 다른 쇼핑몰에 연결되어 있어요 — 담당자에게 문의해 주세요'
    case 'concurrent_callback':
      return '연동이 동시에 두 번 진행됐어요. 새로고침해 상태를 확인해 주세요'
    default:
      return '카카오모먼트 연동에 실패했어요. 다시 시도해 주세요'
  }
}
